import { Link, useLocation } from 'react-router';
import { Activity } from 'lucide-react';
import { EnterpriseWorkspaceNav } from '../components/enterprise/EnterpriseWorkspaceNav';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useEnterpriseWorkspaceObservability } from '../hooks/use-enterprise-workspace-observability';
import { workspacePath } from '../lib/enterprise-work-routes';

export default function EnterpriseWorkspaceObservability() {
  const location = useLocation();
  const { loading, error, data } = useEnterpriseWorkspaceObservability();

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,#020617_0%,#0b1220_100%)]">
      <EnterpriseWorkspaceNav />
      <div className="mx-auto max-w-5xl space-y-4 px-4 py-6">
        <header className="rounded-xl border border-slate-700 bg-slate-900/80 p-5">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5 text-cyan-300" />
            <h1 className="text-2xl font-bold text-slate-100">Observability</h1>
          </div>
          {data?.scope ? <p className="mt-2 text-xs text-blue-300">Scope: {data.scope}</p> : null}
          <p className="mt-1 text-sm text-slate-300">Operational health, readiness, and governance telemetry.</p>
        </header>

        {loading ? <p className="text-sm text-slate-300">Loading observability signals…</p> : null}
        {error ? <p className="text-sm text-amber-300">{error}</p> : null}

        <div className="grid gap-3 md:grid-cols-3">
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">Risk score</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">{data ? data.scorecard.riskScore : '—'}</p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">30d high rate</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">{data ? `${data.scorecard.highRate30}%` : '—'}</p>
          </Card>
          <Card className="border-slate-700 bg-slate-900/80 p-4">
            <p className="text-xs uppercase tracking-wide text-slate-400">SLA breaches (30d)</p>
            <p className="mt-2 text-2xl font-semibold text-slate-100">{data ? data.scorecard.slaBreaches30 : '—'}</p>
          </Card>
        </div>

        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Readiness checks</p>
          <ul className="mt-2 space-y-1 text-sm text-slate-300">
            <li>Policy coverage: {data?.readiness.policyCoverage ? 'pass' : 'needs attention'}</li>
            <li>Response SLA configured: {data?.readiness.responseSlaConfigured ? 'pass' : 'needs attention'}</li>
            <li>Queue within SLA: {data?.readiness.queueWithinSla ? 'pass' : 'breaches detected'}</li>
            <li>Activity cadence healthy: {data?.readiness.activityCadenceHealthy ? 'pass' : 'insufficient signal volume'}</li>
          </ul>
        </Card>

        <Card className="border-slate-700 bg-slate-900/80 p-4">
          <p className="text-sm font-semibold text-slate-100">Recent governance events</p>
          <ul className="mt-2 space-y-2 text-sm text-slate-300">
            {!data || data.auditEvents.length === 0 ? (
              <li>No audit events available.</li>
            ) : (
              data.auditEvents.slice(0, 10).map((event) => (
                <li key={event.id}>
                  <span className="font-semibold text-slate-100">{event.action}</span> · {new Date(event.created_at).toLocaleString()}
                </li>
              ))
            )}
          </ul>
        </Card>

        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to={workspacePath(location.pathname, 'settings')}>Open settings</Link>
          </Button>
          <Button asChild variant="outline" className="border-slate-600 bg-slate-900 text-slate-100">
            <Link to={workspacePath(location.pathname, 'interventions')}>Open interventions</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

